/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';

interface SharedVerticalZoom {
  scale: number;
  y: number;
  sourceId: string | null;
}

interface MouseYCtx {
  clientY: number | null;
  verticalZoom: SharedVerticalZoom;
  publishVerticalZoom: (sourceId: string, scale: number, y: number) => void;
  resetVerticalZoom: (sourceId: string) => void;
}

const defaultVerticalZoom: SharedVerticalZoom = {
  scale: 1,
  y: 0,
  sourceId: null,
};

const MouseYContext = React.createContext<MouseYCtx>({
  clientY: null,
  verticalZoom: defaultVerticalZoom,
  publishVerticalZoom: () => {},
  resetVerticalZoom: () => {},
});
export const useSharedMouseY = (): MouseYCtx => React.useContext(MouseYContext);

/**
 * Wraps the area containing Soundings + WindBarbs so both can react
 * to the same vertical mouse position (X ignored).
 */
export const SharedMouseYProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [clientY, setClientY] = React.useState<number | null>(null);
  const [verticalZoom, setVerticalZoom] =
    React.useState<SharedVerticalZoom>(defaultVerticalZoom);

  const publishVerticalZoom = React.useCallback(
    (sourceId: string, scale: number, y: number): void => {
      setVerticalZoom((previous) => {
        const hasSameValues =
          previous.sourceId === sourceId &&
          Math.abs(previous.scale - scale) < 1e-6 &&
          Math.abs(previous.y - y) < 1e-3;

        if (hasSameValues) {
          return previous;
        }

        return {
          scale,
          y,
          sourceId,
        };
      });
    },
    [],
  );

  const resetVerticalZoom = React.useCallback((sourceId: string): void => {
    setVerticalZoom(() => ({
      scale: 1,
      y: 0,
      sourceId,
    }));
  }, []);

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const onMove = (e: MouseEvent) => setClientY(e.clientY);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const onLeave = () => setClientY(null);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    // eslint-disable-next-line consistent-return, @typescript-eslint/explicit-function-return-type
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);
  const contextValue = React.useMemo(
    () => ({
      clientY,
      verticalZoom,
      publishVerticalZoom,
      resetVerticalZoom,
    }),
    [clientY, verticalZoom, publishVerticalZoom, resetVerticalZoom],
  );

  return (
    <div
      data-testid="shared-mouse-y-wrapper"
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <MouseYContext.Provider value={contextValue}>
        {children}
      </MouseYContext.Provider>
    </div>
  );
};
